import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Modal from 'components/Modal'
import Button from 'components/Button'
import { TextField, MenuItem, Tooltip } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectActiveStudents } from 'modules/students/ducks/selectors'
import { addStudentPayment } from 'services/StudentsService'
import { removePaymentProblem } from 'services/StudentPaymentProblemsService'
import { dateWithTime, DATE_FORMATS } from 'utils/date'
import { propOr } from 'ramda'
import { parseNumber } from 'utils/numbers'
import { v4 as uuid } from 'uuid'
import { toast } from 'react-hot-toast'
import moment from 'moment'
import PaymentIcon from '@mui/icons-material/Payment'

const parseAmountFromInput = str => {
  if (str == null || String(str).trim() === '') return NaN
  return parseFloat(String(str).replace(/\s/g, '').replace(',', '.'))
}

const problemDateToInputValue = raw => {
  if (raw == null || String(raw).trim() === '') return ''
  const strict = moment(
    raw,
    [
      DATE_FORMATS.input,
      DATE_FORMATS.dashed,
      'DD.MM.YYYY',
      'D.M.YYYY',
      'DD/MM/YYYY',
      'YYYY/MM/DD'
    ],
    true
  )
  if (strict.isValid()) return strict.format(DATE_FORMATS.input)
  const loose = moment(raw)
  return loose.isValid() ? loose.format(DATE_FORMATS.input) : ''
}

const AddPaymentProblemModal = ({ problem, onSuccess }) => {
  const [open, setOpen] = useState(false)
  const students = useSelector(selectActiveStudents)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [dateInput, setDateInput] = useState('')
  const [amountStr, setAmountStr] = useState('')

  useEffect(() => {
    if (open && problem) {
      setDateInput(problemDateToInputValue(problem.date))
      const a = parseNumber(problem.amount)
      setAmountStr(Number.isFinite(a) ? a.toFixed(2) : '')
    }
  }, [open, problem])

  const handleAddPayment = async () => {
    if (!selectedStudent || !problem) return
    const amount = parseAmountFromInput(amountStr)
    if (!dateInput || Number.isNaN(amount) || amount <= 0) {
      toast.error('Uzupełnij poprawną datę i kwotę')
      return
    }
    const dateForDebit = moment(dateInput, DATE_FORMATS.input, true)
    if (!dateForDebit.isValid()) {
      toast.error('Niepoprawna data')
      return
    }
    setIsSaving(true)
    try {
      const currentBalance = parseNumber(propOr(0, 'balance', selectedStudent))
      const newBalance = currentBalance + amount

      const debitPayload = {
        type: 'payment',
        studentId: selectedStudent.id,
        comment: '',
        createdAt: dateWithTime(new Date()),
        id: uuid(),
        value: amount,
        date: dateWithTime(dateForDebit.toDate()),
        balanceAfter: newBalance
      }

      const studentPayload = {
        ...selectedStudent,
        balance: newBalance
      }

      await addStudentPayment({
        values: { debitPayload, studentPayload },
        suppressToast: true
      })

      await removePaymentProblem(problem.id)
      toast.success('Dodano wpłatę')
      onSuccess?.()
      handleClose()
    } catch (err) {
      toast.error('Wystąpił błąd')
      console.error(err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleOpen = e => {
    e?.stopPropagation?.()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedStudent(null)
    setDateInput('')
    setAmountStr('')
  }

  if (!problem) return null

  return (
    <>
      <Tooltip title='Dodaj wpłatę'>
        <Trigger onClick={handleOpen}>
          <StyledIcon />
        </Trigger>
      </Tooltip>
      <Modal open={open} onClose={handleClose} title='Dodaj wpłatę'>
      <Content>
        <ProblemSummary>
          <FieldsRow>
            <TextField
              type='date'
              label='Data wpłaty'
              value={dateInput}
              onChange={e => setDateInput(e.target.value)}
              variant='outlined'
              size='small'
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label='Kwota (zł)'
              value={amountStr}
              onChange={e => setAmountStr(e.target.value)}
              variant='outlined'
              size='small'
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
            />
          </FieldsRow>
          <Row>
            <Label>Tytuł:</Label> {problem.title}
          </Row>
        </ProblemSummary>
        <SelectWrapper>
          <TextField
            select
            fullWidth
            label='Uczeń'
            value={selectedStudent?.id ?? ''}
            onChange={e => {
              const id = e.target.value
              setSelectedStudent(id ? students.find(s => s.id === id) : null)
            }}
            variant='outlined'
            size='small'
          >
            <MenuItem value=''>—</MenuItem>
            {students.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
        </SelectWrapper>
      </Content>
      <ButtonsWrapper>
        <Button color='secondary' onClick={handleClose}>
          Anuluj
        </Button>
        <Button
          color='primary'
          onClick={handleAddPayment}
          disabled={!selectedStudent || !dateInput || isSaving}
        >
          {isSaving ? 'Zapisywanie...' : 'Dodaj wpłatę'}
        </Button>
      </ButtonsWrapper>
    </Modal>
    </>
  )
}

export default AddPaymentProblemModal

const Content = styled.div`
  padding: 20px 0;
`

const ProblemSummary = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: ${({ theme }) => (theme.colors.lightGrey || '#f5f5f5')};
  border-radius: 8px;
`

const FieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`

const Row = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
`

const Label = styled.span`
  font-weight: 600;
  margin-right: 8px;
`

const SelectWrapper = styled.div`
  margin-bottom: 16px;
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border || '#eee'};
`

const Trigger = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`

const StyledIcon = styled(PaymentIcon)`
  transition: all 0.3s;

  &:hover {
    color: ${({ theme }) => theme.colors.primary?.main || '#1976d2'};
  }
`
